import { useEffect, useRef, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Plus, Check, X, Trash, Camera, DownloadSimple, CaretDown, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { isoDateIST } from "../lib/utils";
import { DateInput } from "../components/ui/date-input";
import { ExpenseClaimDrawer } from "../components/ExpenseClaimDrawer";
import { ExpensePaymentDialog } from "../components/ExpensePaymentDialog";

const ADMIN_ROLES = new Set(["super_admin","company_admin","country_head","region_head","branch_manager","sub_manager","assistant_manager"]);
const FINANCE_ROLES = new Set(["super_admin","company_admin","country_head"]);
const CATS = ["travel_flight","travel_hotel","travel_taxi","travel_mileage","travel_per_diem","meals","client_meeting","office_supplies","subscription","training","phone_internet","fuel","medical","other"];
const STATUS_COLOR = {
  draft:"bg-zinc-100 text-zinc-700",
  submitted:"bg-amber-100 text-amber-800",
  approved:"bg-emerald-100 text-emerald-800",
  rejected:"bg-red-100 text-red-800",
  reimbursed:"bg-blue-100 text-blue-800",
  booked:"bg-purple-100 text-purple-800",
  completed:"bg-zinc-100 text-zinc-700",
  cancelled:"bg-zinc-100 text-zinc-600",
};
const RISK_PILL = {
  high:   { cls: "bg-red-100 text-red-700 border-red-200",       label: "High risk" },
  medium: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Medium" },
  low:    { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Clean" },
};
function RiskPill({ fraud, onClick, testid }) {
  if (!fraud || typeof fraud.risk_score !== "number") return null;
  const t = fraud.tier || "low";
  const c = RISK_PILL[t] || RISK_PILL.low;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Risk ${fraud.risk_score}/100 — ${(fraud.signals || []).length} signal(s). Click for details.`}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 hover:opacity-80 transition ${c.cls}`}
    >
      <span className="font-bold tabular-nums">{fraud.risk_score}</span>
      <span>{c.label}</span>
    </button>
  );
}
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Expenses() {
  const [tab, setTab] = useState("claims");
  const tabs = [
    { k: "claims",    l: "Expense claims" },
    { k: "analytics", l: "Analytics" },
  ];
  return (
    <AppShell title="Expenses">
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`exp-tab-${t.k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === t.k ? "border-zinc-950 text-zinc-950 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>{t.l}</button>
        ))}
      </div>
      {tab === "claims" && <ClaimsTab/>}
      {tab === "analytics" && <AnalyticsTab/>}
    </AppShell>
  );
}


// ────────────────────── Claims tab ──────────────────────

function ClaimsTab() {
  const { user } = useAuth();
  const canApprove = ADMIN_ROLES.has(user?.role);
  const canFinance = FINANCE_ROLES.has(user?.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [fraudDialog, setFraudDialog] = useState(null); // {claim, fraud, busy}
  const [openClaimId, setOpenClaimId] = useState(null);
  const [payClaims, setPayClaims] = useState(null);
  const downloadVoucher = async (id) => {
    try {
      const res = await api.get(`/expenses/${id}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `voucher_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("Voucher download failed"); }
  };

  // New-claim form
  const blankItem = () => ({ category: "meals", expense_date: isoDateIST(), amount: 0, description: "" });
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [items, setItems] = useState([blankItem()]);

  const load = async () => { try { const r = await api.get("/expenses"); setRows(r.data); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);

  const addItem = () => setItems([...items, blankItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  // Receipt OCR — fills the form from a snapped image
  const fileRef = useRef(null);
  const onReceiptPicked = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/expenses/scan-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data?.data || {};
      if (!r.data?.ok) {
        toast.error(r.data?.error || "Couldn't read the receipt");
        return;
      }
      if (!open) setOpen(true);
      if (d.vendor_name) setTitle(prev => prev || `${d.vendor_name} bill`);
      if (d.category_guess) setPurpose(prev => prev || d.category_guess);
      const newItem = {
        category: mapGuessToCategory(d.category_guess) || "meals",
        expense_date: d.date || isoDateIST(),
        amount: d.total_amount || 0,
        description: d.vendor_name ? `${d.vendor_name}${d.gstin ? ` · GSTIN ${d.gstin}` : ""}` : "",
      };
      // If there are clean line items, replace the form; otherwise add the total as one row.
      if (Array.isArray(d.line_items) && d.line_items.length > 0) {
        const li = d.line_items.map(li => ({
          category: newItem.category,
          expense_date: newItem.expense_date,
          amount: Number(li.amount) || 0,
          description: li.description || newItem.description,
        }));
        setItems(li.length > 0 ? li : [newItem]);
      } else {
        setItems([newItem]);
      }
      toast.success(`Receipt scanned — confidence: ${d.confidence || "—"}. Please review.`);
      const dup = r.data?.duplicate_of;
      if (dup) {
        toast.warning(
          dup.same_employee
            ? `⚠️ You've already claimed this receipt — see claim ${(dup.claim_id || "").slice(0, 8)}`
            : `⚠️ ${dup.employee_name || "Another employee"} already claimed this exact receipt`,
          { duration: 8000 }
        );
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Receipt scan failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      const r = await api.post("/expenses", { title, purpose, items });
      await api.post(`/expenses/${r.data.id}/submit`);
      toast.success("Claim created & submitted");
      setOpen(false); setTitle(""); setPurpose(""); setItems([blankItem()]);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const decide = async (id, decision) => {
    try {
      await api.post(`/expenses/${id}/decide`, { decision, reason: decision === "reject" ? (prompt("Reason?") || "") : undefined });
      toast.success(`Claim ${decision}d`); load(); setOpenClaimId(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Bulk actions
  const toggle = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const toggleAll = (status) => {
    const ids = rows.filter(r => r.status === status).map(r => r.id);
    const all = ids.every(id => selected.has(id));
    setSelected(new Set(all ? [...selected].filter(x => !ids.includes(x)) : [...selected, ...ids]));
  };
  const bulkReimburse = async () => {
    const ids = [...selected];
    if (!ids.length) return toast.error("Select at least one claim");
    if (!confirm(`Mark ${ids.length} claim(s) as reimbursed?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/expenses/bulk-reimburse", { claim_ids: ids });
      toast.success(`Marked ${r.data.marked} as reimbursed`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const exportNeft = async () => {
    try {
      const res = await api.get("/expenses/exports/neft?status=approved", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `neft_${isoDateIST()}.csv`; a.click();
      toast.success("NEFT CSV downloaded");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const pushOneToPayroll = async (id) => {
    try { const r = await api.post(`/expenses/${id}/push-to-payroll`); toast.success(r.data.run_id === "PENDING" ? "Queued for next payroll run" : `Added to payroll run ${r.data.run_id.slice(0,8)}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const removeFromPayroll = async (id) => {
    if (!window.confirm("Take this claim out of payroll? It goes back to 'approved' so you can pay it directly.")) return;
    try {
      const r = await api.post(`/expenses/${id}/remove-from-payroll`);
      toast.success(r.data.recompute_needed ? "Removed — recompute the payroll run to update payslips" : "Removed from payroll");
      load(); setOpenClaimId(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const payable = (r) => r.status === "approved" && !r.pushed_to_payroll_run_id;
  const selectEmployee = (empId) => {
    const ids = rows.filter(r => payable(r) && r.employee_id === empId).map(r => r.id);
    setSelected(new Set([...selected, ...ids]));
  };

  const total = items.reduce((s, i) => s + (+i.amount || 0), 0);
  const pending = rows.filter(r => r.status === "submitted").length;
  const approved = rows.filter(r => r.status === "approved").length;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total claims" value={rows.length}/>
        <StatCard label="Pending review" value={pending}/>
        <StatCard label="Approved" value={approved}/>
        <StatCard label="Reimbursed" value={rows.filter(r => r.status === "reimbursed").length}/>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 bg-zinc-950 text-white rounded-md px-4 py-2.5 flex items-center justify-between" data-testid="exp-bulk-bar">
          <div className="text-sm"><strong>{selected.size}</strong> claim{selected.size > 1 ? "s" : ""} selected</div>
          <div className="flex gap-2">
            {canFinance && <Button size="sm" className="h-8 bg-white text-zinc-900 hover:bg-zinc-100" onClick={() => setPayClaims(rows.filter(r => selected.has(r.id)))} data-testid="exp-bulk-pay">Record payment (Cash / UPI / Bank)</Button>}
            {canFinance && <Button size="sm" variant="outline" className="h-8 text-zinc-900" disabled={busy} onClick={bulkReimburse} data-testid="exp-bulk-reimburse">Mark reimbursed</Button>}
            <Button size="sm" variant="outline" className="h-8 text-zinc-900" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        </div>
      )}

      <SectionCard title="Claims inbox" subtitle="Employees submit; admin approves or rejects; finance reimburses."
        action={
          <div className="flex gap-2 items-center">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => onReceiptPicked(e.target.files?.[0])} data-testid="exp-receipt-file"/>
            <Button size="sm" variant="outline" className="gap-1.5 h-9" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="exp-scan-receipt-btn">
              {busy ? <ArrowsClockwise size={14} className="animate-spin"/> : <Camera size={14}/>}
              {busy ? "Scanning…" : "Scan receipt"}
              <Sparkle size={10} weight="fill" className="text-amber-500"/>
            </Button>
            {canFinance && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5 h-9" data-testid="exp-finance-menu"><DownloadSimple size={14}/> Finance <CaretDown size={12}/></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportNeft} data-testid="exp-export-neft">Download NEFT CSV (approved)</DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => { try { const res = await api.get("/expenses/exports/neft?status=reimbursed", { responseType: "blob" }); const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" })); const a = document.createElement("a"); a.href = url; a.download = `neft_reimbursed_${isoDateIST()}.csv`; a.click(); toast.success("Downloaded"); } catch (e) { toast.error("Export failed"); } }}>Download NEFT CSV (already reimbursed)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button size="sm" onClick={() => setOpen(true)} data-testid="exp-new-btn" className="gap-1.5 h-9 bg-zinc-950 hover:bg-zinc-800"><Plus size={14} weight="bold"/> New claim</Button>
          </div>
        }>
        <Table>
          <TableHeader><TableRow>
            {canFinance && <TableHead className="w-8"></TableHead>}
            <TableHead>Title</TableHead><TableHead>Employee</TableHead><TableHead>Status</TableHead><TableHead>Risk</TableHead>
            <TableHead className="text-right">Amount</TableHead><TableHead>Items</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={canFinance ? 8 : 7} className="text-center text-zinc-500 py-6">No claims yet.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id} className={`cursor-pointer hover:bg-zinc-50 ${selected.has(r.id) ? "bg-zinc-50" : ""}`}
                onClick={() => setOpenClaimId(r.id)} data-testid={`exp-row-${r.id}`}>
                {canFinance && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {payable(r) && (
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`exp-row-select-${r.id}`}/>
                    )}
                  </TableCell>
                )}
                <TableCell><div className="font-medium">{r.title}</div><div className="text-xs text-zinc-500">{r.purpose}</div></TableCell>
                <TableCell>
                  <div>{r.employee_name}</div>
                  {canFinance && payable(r) && rows.filter(x => payable(x) && x.employee_id === r.employee_id).length > 1 && (
                    <button type="button" className="text-[11px] text-blue-700 hover:underline" onClick={(e) => { e.stopPropagation(); selectEmployee(r.employee_id); }}
                      data-testid={`exp-select-emp-${r.id}`}>Select all for {r.employee_name?.split(" ")[0]}</button>
                  )}
                </TableCell>
                <TableCell><Badge className={STATUS_COLOR[r.status]}>{r.status}</Badge></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {r.fraud ? <RiskPill fraud={r.fraud} onClick={() => setFraudDialog({ claim: r, fraud: r.fraud })} testid={`exp-risk-${r.id}`}/> : (
                    canApprove ? <button className="text-[10px] text-zinc-400 hover:text-zinc-700" onClick={async () => { try { const f = await api.post(`/expenses/${r.id}/recheck-fraud`, {}); setFraudDialog({ claim: r, fraud: f.data }); load(); } catch (e) { toast.error("Recheck failed"); } }} data-testid={`exp-risk-check-${r.id}`}>Check</button> : null
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{inr(r.total_amount)}</TableCell>
                <TableCell className="text-xs text-zinc-500">
                  {r.items?.length || 0} items
                  {r.payment && <div className="text-[10px] text-blue-700 uppercase" data-testid={`exp-paid-via-${r.id}`}>Paid · {r.payment.mode.replace("_", " ")}</div>}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end flex-wrap">
                    {canApprove && r.status === "submitted" && (<>
                      <Button size="sm" onClick={() => decide(r.id, "approve")} className="gap-1" data-testid={`exp-approve-${r.id}`}><Check size={12}/> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")} className="gap-1" data-testid={`exp-reject-${r.id}`}><X size={12}/> Reject</Button>
                    </>)}
                    {canFinance && r.status === "approved" && !r.pushed_to_payroll_run_id && (<>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setPayClaims([r])} data-testid={`exp-pay-${r.id}`}>Pay now</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => pushOneToPayroll(r.id)} data-testid={`exp-push-payroll-${r.id}`}>→ Payroll</Button>
                    </>)}
                    {canFinance && r.status === "approved" && r.pushed_to_payroll_run_id && (<>
                      <span className="text-[10px] uppercase text-purple-700 self-center" data-testid={`exp-in-payroll-${r.id}`}>In payroll</span>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => removeFromPayroll(r.id)} data-testid={`exp-unpayroll-${r.id}`}>Undo payroll</Button>
                    </>)}
                    {(r.status === "approved" || r.status === "reimbursed") && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`voucher-${r.id}`}
                        onClick={() => downloadVoucher(r.id)}>Voucher</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <ExpenseClaimDrawer claimId={openClaimId} onClose={() => setOpenClaimId(null)} canApprove={canApprove} canFinance={canFinance}
        onDecide={decide} onVoucher={downloadVoucher} onPayroll={(id) => { pushOneToPayroll(id); setOpenClaimId(null); }}
        onUnpayroll={removeFromPayroll}
        onPay={(c) => { setOpenClaimId(null); setPayClaims([c]); }}/>
      <ExpensePaymentDialog claims={payClaims} onClose={() => setPayClaims(null)} onDone={() => { setPayClaims(null); setSelected(new Set()); load(); }}/>

      <Dialog open={!!fraudDialog} onOpenChange={() => setFraudDialog(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="fraud-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Fraud risk review {fraudDialog?.fraud && <RiskPill fraud={fraudDialog.fraud}/>} </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-600 mb-2">
            Claim <span className="font-medium">{fraudDialog?.claim?.title}</span> · {inr(fraudDialog?.claim?.total_amount)} · {fraudDialog?.claim?.employee_name}
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-auto">
            {(fraudDialog?.fraud?.signals || []).length === 0 && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">No fraud signals detected. ✓</div>
            )}
            {(fraudDialog?.fraud?.signals || []).map((s, idx) => (
              <div key={idx} className={`border rounded p-3 ${s.severity === 3 ? "bg-red-50 border-red-200" : s.severity === 2 ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200"}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs font-semibold uppercase tracking-wide">{s.code}</div>
                  <div className="text-[10px] text-zinc-500">severity {s.severity}/3</div>
                </div>
                <div className="text-sm">{s.label}</div>
                {s.detail && Object.keys(s.detail).length > 0 && (
                  <pre className="mt-2 text-[11px] text-zinc-600 bg-white/60 border rounded p-2 overflow-auto">{JSON.stringify(s.detail, null, 2)}</pre>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            {canApprove && (
              <Button variant="outline" disabled={fraudDialog?.busy} onClick={async () => {
                setFraudDialog((d) => ({ ...d, busy: true }));
                try {
                  const r = await api.post(`/expenses/${fraudDialog.claim.id}/recheck-fraud`, { run_vision: true });
                  setFraudDialog({ claim: fraudDialog.claim, fraud: r.data });
                  toast.success("Re-checked with DeepSeek vision");
                  load();
                } catch (e) { toast.error("Re-check failed"); setFraudDialog((d) => ({ ...d, busy: false })); }
              }} data-testid="fraud-recheck-vision-btn">
                <Sparkle size={12} weight="fill" className="mr-1 text-amber-500"/>
                {fraudDialog?.busy ? "Running AI vision…" : "Re-check with AI vision"}
              </Button>
            )}
            <Button onClick={() => setFraudDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>New expense claim</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-auto pr-1">
            <div className="bg-amber-50/60 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2">
              <Sparkle size={14} weight="fill" className="text-amber-600 mt-0.5"/>
              <div>Snap a photo of your receipt and we'll auto-fill the form. Use the <strong>Scan receipt</strong> button on the page header.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="March client visits" data-testid="exp-title"/></div>
              <div><Label>Purpose</Label><Input value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="Q4 business development" data-testid="exp-purpose"/></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Items</Label><Button size="sm" variant="outline" onClick={addItem} className="gap-1"><Plus size={12}/>Add item</Button></div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start border p-2 rounded-lg">
                    <Select value={it.category} onValueChange={v => updateItem(i, "category", v)}>
                      <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{CATS.map(c => (<SelectItem key={c} value={c} className="capitalize text-xs">{c.replace(/_/g, " ")}</SelectItem>))}</SelectContent>
                    </Select>
                    <DateInput value={it.expense_date} onChange={e => updateItem(i, "expense_date", e.target.value)} className="col-span-3 h-8 text-xs"/>
                    <Input type="number" value={it.amount} onChange={e => updateItem(i, "amount", +e.target.value)} placeholder="Amount" className="col-span-2 h-8 text-xs"/>
                    <Input value={it.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Description" className="col-span-3 h-8 text-xs"/>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(i)} className="col-span-1 h-8 px-2"><Trash size={14}/></Button>
                  </div>
                ))}
              </div>
              <p className="text-right text-sm font-semibold mt-2">Total: {inr(total)}</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="exp-save-btn">Save & submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function mapGuessToCategory(guess) {
  const g = (guess || "").toLowerCase();
  if (g.includes("food") || g.includes("meal") || g.includes("restaurant")) return "meals";
  if (g.includes("hotel")) return "travel_hotel";
  if (g.includes("flight") || g.includes("air")) return "travel_flight";
  if (g.includes("cab") || g.includes("taxi") || g.includes("uber") || g.includes("ola")) return "travel_taxi";
  if (g.includes("fuel")) return "fuel";
  if (g.includes("office") || g.includes("stationery")) return "office_supplies";
  if (g.includes("phone") || g.includes("internet") || g.includes("data")) return "phone_internet";
  if (g.includes("subscription") || g.includes("software") || g.includes("saas")) return "subscription";
  if (g.includes("training") || g.includes("course")) return "training";
  return "other";
}


// ────────────────────── Analytics tab ──────────────────────

const PIE_COLORS = ["#09090b", "#3f3f46", "#71717a", "#a1a1aa", "#d4d4d8", "#f59e0b", "#10b981", "#3b82f6"];

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/expenses/analytics/overview"); setData(r.data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-zinc-500 text-sm p-8 text-center">Loading analytics…</div>;

  const k = data.kpis || {};
  return (
    <div className="space-y-5" data-testid="exp-analytics-root">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total claimed" value={inr(k.total_claimed?.amount || 0)} caption={`${k.total_claimed?.count || 0} claims`}/>
        <StatCard label="Pending review" value={inr(k.pending?.amount || 0)} caption={`${k.pending?.count || 0} claims`}/>
        <StatCard label="Approved" value={inr(k.approved?.amount || 0)} caption={`${k.approved?.count || 0} claims`}/>
        <StatCard label="Reimbursed" value={inr(k.reimbursed?.amount || 0)} caption={`${k.reimbursed?.count || 0} claims`}/>
        <StatCard label="Avg approval TAT" value={k.avg_approval_hours == null ? "—" : `${k.avg_approval_hours}h`} caption="submitted → approved"/>
      </div>

      <SectionCard title="Monthly spend trend" subtitle="Approved + reimbursed claims, last 12 months">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.monthly_trend} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7"/>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#71717a"/>
              <YAxis tick={{ fontSize: 11 }} stroke="#71717a" tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}/>
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ fontSize: 12 }}/>
              <Line type="monotone" dataKey="amount" stroke="#09090b" strokeWidth={2} dot={{ fill: "#09090b", r: 3 }} activeDot={{ r: 5 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-5">
        <SectionCard title="Top categories" subtitle="Where money is going">
          {(data.categories || []).length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No data yet.</div> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.categories} dataKey="amount" nameKey="name" outerRadius={90} innerRadius={50}>
                    {data.categories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Top spenders" subtitle="People with most claims this period">
          {(data.top_spenders || []).length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No data yet.</div> : (
            <div className="space-y-2 py-2">
              {data.top_spenders.map((t, i) => (
                <div key={t.employee_id} className="flex items-center gap-3" data-testid={`exp-top-spender-${i}`}>
                  <div className="text-xs font-mono text-zinc-400 w-5">{i + 1}</div>
                  <div className="flex-1 truncate text-sm">{t.name}</div>
                  <div className="text-xs text-zinc-500 tabular-nums">{t.count}</div>
                  <div className="text-sm font-semibold tabular-nums">{inr(t.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="By branch" subtitle="Where claims are coming from">
        {(data.by_branch || []).length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No data yet.</div> : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_branch} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7"/>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#71717a"/>
                <YAxis tick={{ fontSize: 11 }} stroke="#71717a" tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}/>
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ fontSize: 12 }}/>
                <Bar dataKey="amount" fill="#09090b" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
